import React, { useState, useEffect, useCallback } from 'react';
import type { RiverDOMEvent } from '../services/worldEventsService';
import type { ContextSource } from '../components/Space/ContextualAIModal/ContextualAIModal';
import { AmbientSuggestionBubble } from '../components/AmbientSuggestion/AmbientSuggestionBubble';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('AMBIENT_SUGGESTIONS', { enabled: false });

// ---------------------------------------------------------------------------
// Public types — exported so ContextualAIModal and useGlobalCmdKShortcut can use them
// ---------------------------------------------------------------------------

export interface AmbientActionCard {
  id: string;
  title: string;
  subtitle?: string;
  connectPrompt: string;
}

export interface AmbientSuggestion {
  id: string;
  headline: string;
  body: string;
  cta: string;
  modalMessage: string;
  cards: AmbientActionCard[];
  prefilledPrompt: string;
  context: ContextSource;
  salience: 'low' | 'medium' | 'high';
  ttl?: number;
  // Optional fields added by the intelligence layer (all backward-compatible)
  category?: 'follow_up' | 'deadline' | 'insight' | 'pattern';
  reason?: string;
  source?: 'local' | 'backend';
  entityId?: string;
}

// ---------------------------------------------------------------------------
// SuggestionRule — extensible rule interface
// ---------------------------------------------------------------------------

interface SuggestionRule {
  eventType: string;
  condition: (event: RiverDOMEvent) => boolean;
  salience: 'low' | 'medium' | 'high';
  generate: (event: RiverDOMEvent) => AmbientSuggestion | null;
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

const VAULT_IMPORT_RULE: SuggestionRule = {
  eventType: 'vault.import.completed',
  condition: (e) => (e.data.created ?? 0) > 0,
  salience: 'medium',
  generate: (e) => {
    const { created = 0, format = 'unknown', matchedServices = [] } = e.data ?? {};
    const label = format === 'dashlane-zip' ? 'Dashlane' : format;

    const cards: AmbientActionCard[] = (matchedServices as Array<{ integrationId: string; name: string; count: number }>)
      .slice(0, 5)
      .map((svc) => ({
        id: svc.integrationId,
        title: svc.name,
        subtitle: svc.count > 1 ? `${svc.count} accounts` : undefined,
        connectPrompt: `Connect my ${svc.name} integration. I have credentials for ${svc.integrationId} in my vault from a recent ${label} import. Please guide me through connecting it.`,
      }));

    const modalMessage =
      cards.length > 0
        ? `I noticed you just imported ${created} credentials from ${label}. I found ${cards.length} service${cards.length > 1 ? 's' : ''} with available River integrations:`
        : `I noticed you just imported ${created} credentials from ${label}. Would you like me to check which services have River integrations you can connect?`;

    return {
      id: `vault-import-${Date.now()}`,
      headline: `${created} credentials imported from ${label}`,
      body:
        cards.length > 0
          ? `Found ${cards.length} integration${cards.length > 1 ? 's' : ''} you could connect`
          : 'Want me to check for available integrations?',
      cta: cards.length > 0 ? 'Connect integrations' : 'Check integrations',
      modalMessage,
      cards,
      prefilledPrompt:
        cards.length > 0
          ? `I just imported ${created} credentials from ${label}. I want to connect: ${cards.map((c) => c.title).join(', ')}. Please help me connect each one.`
          : `I just imported ${created} credentials from ${label}. Please help me identify which services have River integrations available and guide me through connecting them.`,
      context: { type: 'windows', windowIds: [] },
      salience: 'medium',
      ttl: 90_000,
    };
  },
};

export const SUGGESTION_RULES: SuggestionRule[] = [VAULT_IMPORT_RULE];

const SALIENCE_ORDER: Record<AmbientSuggestion['salience'], number> = {
  high: 3,
  medium: 2,
  low: 1,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseAmbientSuggestionsOptions {
  onAct: (suggestion: AmbientSuggestion) => void;
}

export const useAmbientSuggestions = ({ onAct }: UseAmbientSuggestionsOptions) => {
  const [suggestions, setSuggestions] = useState<AmbientSuggestion[]>([]);

  const dismiss = useCallback((id: string, reason: 'user' | 'ttl' = 'user') => {
    log.info('[AmbientSuggestions] dismissed', { id, reason });
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const event = (e as CustomEvent<RiverDOMEvent>).detail;
      if (!event?.type) return;

      for (const rule of SUGGESTION_RULES) {
        if (rule.eventType !== event.type) continue;
        if (!rule.condition(event)) continue;
        const suggestion = rule.generate(event);
        if (!suggestion) continue;

        setSuggestions((prev) => {
          if (prev.find((s) => s.id === suggestion.id)) return prev; // dedup
          log.info('[AmbientSuggestions] queued', {
            id: suggestion.id,
            headline: suggestion.headline,
            cards: suggestion.cards.length,
            salience: suggestion.salience,
          });
          return [...prev, suggestion];
        });

        if (suggestion.ttl) {
          setTimeout(() => dismiss(suggestion.id, 'ttl'), suggestion.ttl);
        }
      }
    };

    window.addEventListener('river:worldevent', handler);
    return () => window.removeEventListener('river:worldevent', handler);
  }, [dismiss]);

  const handleAct = useCallback(
    (suggestion: AmbientSuggestion) => {
      log.info('[AmbientSuggestions] acted', { id: suggestion.id, headline: suggestion.headline });
      onAct(suggestion);
      dismiss(suggestion.id, 'user');
    },
    [onAct, dismiss]
  );

  // Show only the highest-salience suggestion at a time
  const activeSuggestion = suggestions
    .slice()
    .sort((a, b) => SALIENCE_ORDER[b.salience] - SALIENCE_ORDER[a.salience])[0] ?? null;

  const AmbientBubble = activeSuggestion ? (
    <AmbientSuggestionBubble
      key={activeSuggestion.id}
      suggestion={activeSuggestion}
      onAct={() => handleAct(activeSuggestion)}
      onDismiss={() => dismiss(activeSuggestion.id, 'user')}
    />
  ) : null;

  return { AmbientBubble, suggestions, dismiss };
};
